/**
 * Where to scroll so a section/block sits just below any fixed header, given its position in the
 * *document*.
 *
 * Split out from the DOM call so the geometry is testable, matching the editor canvas's own
 * `getCenteredScrollTop` (apps/app's `presentationCanvasScroll.ts`), which fixed the same bug this
 * guards against: the input here must be `getBoundingClientRect().top + window.scrollY`, **not**
 * `element.offsetTop`. Every section/block `id` div in `Presentation.tsx` renders inside its own
 * `position: relative` wrapper (`classes.sectionWrapper`/`classes.block`), so `offsetTop` reads
 * relative to whichever positioned ancestor happens to be closest rather than the document root —
 * landing the scroll well short of, or past, the real target instead of at it.
 */
export function getPresentationScrollTop(args: { documentTop: number; headerHeight: number; gutter: number }): number {
  const { documentTop, headerHeight, gutter } = args
  return Math.max(0, documentTop - headerHeight - gutter)
}

/**
 * Scrolls `win` so the element with this `key` (a section/block/page id from a rendered
 * presentation) sits below the fixed nav bar. Falls back to the top of the page when `key` isn't
 * on screen (a page key on multi-page nav — pages aren't in the DOM, only the active one renders).
 */
export function scrollToPresentationKey(args: {
  key: string
  headerHeight?: number
  gutter?: number
  doc?: Document
  win?: Window
}): void {
  const { key, headerHeight = 0, gutter = 0, doc = document, win = window } = args
  const element = key ? doc.getElementById(key) : null

  if (!element) {
    win.scrollTo({ top: 0, behavior: 'smooth' })
    return
  }

  const { top } = element.getBoundingClientRect()

  win.scrollTo({
    // viewport rect + scroll, NOT offsetTop — see the jsdoc above
    top: getPresentationScrollTop({ documentTop: top + win.scrollY, headerHeight, gutter }),
    behavior: 'smooth'
  })
}
